import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Badge, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../config/api';

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function formatPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export default function LeadsWidget() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(new Set());
  const { apiCall } = useAuth();

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await apiCall(`${API_BASE_URL}/leads?limit=20`);
      setLeads(response.data || []);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (leadId) => {
    setResolving(prev => new Set(prev).add(leadId));
    try {
      await apiCall(`${API_BASE_URL}/leads/${leadId}/resolve`, { method: 'POST' });
      setLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (error) {
      console.error('Failed to resolve lead:', error);
    } finally {
      setResolving(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  useEffect(() => {
    loadLeads();
    const interval = setInterval(loadLeads, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="shadow-sm">
      <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#0ea5e9' }}>
        <div>
          <i className="fas fa-bullhorn me-2"></i>
          Website Leads
          {leads.length > 0 && (
            <Badge bg="light" text="dark" className="ms-2">{leads.length}</Badge>
          )}
        </div>
        <Button variant="light" size="sm" onClick={loadLeads} disabled={loading}>
          <i className="fas fa-sync-alt"></i>
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center text-muted p-4">
            <i className="fas fa-inbox fa-2x mb-2"></i>
            <p className="mb-0">No leads yet</p>
          </div>
        ) : (
          <ListGroup variant="flush">
            {leads.map((lead) => (
              <ListGroup.Item key={lead.id}>
                <div className="d-flex justify-content-between align-items-start mb-1">
                  <div className="fw-bold">{lead.name}</div>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">{formatDateTime(lead.created_at)}</small>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleResolve(lead.id)}
                      disabled={resolving.has(lead.id)}
                      title="Mark as resolved"
                    >
                      {resolving.has(lead.id) ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="small mb-1">
                  {lead.phone && (
                    <span className="me-3">
                      <i className="fas fa-phone me-1 text-muted"></i>
                      <a href={`tel:${lead.phone}`}>{formatPhone(lead.phone)}</a>
                    </span>
                  )}
                  {lead.email && (
                    <span className="me-3">
                      <i className="fas fa-envelope me-1 text-muted"></i>
                      <a href={`mailto:${lead.email}`}>{lead.email}</a>
                    </span>
                  )}
                </div>
                <div className="small text-muted mb-1">
                  {lead.location && <span className="me-3"><i className="fas fa-map-marker-alt me-1"></i>{lead.location}</span>}
                  {lead.interest && <span className="me-3"><i className="fas fa-car me-1"></i>{lead.interest}</span>}
                  {lead.preferred_contact_method && <span className="me-3"><i className="fas fa-comment-dots me-1"></i>{lead.preferred_contact_method}</span>}
                  {lead.timeframe && <span className="me-3"><i className="fas fa-clock me-1"></i>{lead.timeframe}</span>}
                </div>
                {lead.message && (
                  <div className="small fst-italic text-secondary">"{lead.message}"</div>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
}
